import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { Option, SelectBox } from 'src/components/SelectBox';
import { Textarea } from 'src/components/Textarea';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import type { WorkflowContactWindow } from 'src/interfaces/workflows.interface';
import { formatTimeLabel } from 'src/utils/date';
import { v4 as uuidv4 } from 'uuid';
import type { CampaignInit } from '../campaignWizard.types';

type CreateCampaignDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onNext?: (data: {
    name: string;
    description: string;
    contactWindow: string;
  }) => void;
};

export const CreateCampaignDialog = ({
  isOpen,
  onClose,
  onNext,
}: CreateCampaignDialogProps) => {
  const navigate = useNavigate();
  const { get } = useAuthedRequest();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedContactWindowOption, setSelectedContactWindowOption] =
    useState<Option>({
      label: 'Select contact window',
      value: '',
    });
  const [contactWindowOptions, setContactWindowOptions] = useState<Option[]>(
    [],
  );
  const [contactWindows, setContactWindows] = useState<WorkflowContactWindow[]>(
    [],
  );
  const [isLoadingContactWindows, setIsLoadingContactWindows] = useState(false);

  const [selectedContactWindow, setSelectedContactWindow] =
    useState<WorkflowContactWindow | null>(null);

  const [searchParams] = useSearchParams();
  const campaignName = searchParams.get('campaignName');
  const campaignDescription = searchParams.get('campaignDescription');

  // Populate form fields from URL params when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (campaignName) {
        setName(campaignName);
      }
      if (campaignDescription) {
        setDescription(campaignDescription);
      }
    }
  }, [isOpen, campaignName, campaignDescription]);

  const handleClose = () => {
    setName('');
    setDescription('');
    setSelectedContactWindowOption({
      label: 'Select contact window',
      value: '',
    });
    setSelectedContactWindow(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      setIsLoadingContactWindows(true);
      try {
        const fetchedContactWindows = (await get(
          '/workflow_contact_windows',
        )) as WorkflowContactWindow[];
        const records = Array.isArray(fetchedContactWindows)
          ? fetchedContactWindows
          : [];
        setContactWindows(records);
        const optionItems = records.map((record) => ({
          label: record.label,
          value: record.id,
        }));
        setContactWindowOptions(optionItems);
        const firstRecord =
          records.length > 0 ? (records[0] as WorkflowContactWindow) : null;
        if (firstRecord) {
          setSelectedContactWindowOption({
            label: firstRecord.label,
            value: firstRecord.id,
          });
        } else {
          setSelectedContactWindowOption({
            label: 'Select contact window',
            value: '',
          });
        }
        setSelectedContactWindow(firstRecord || null);
      } catch {
        setContactWindows([]);
        setContactWindowOptions([]);
        setSelectedContactWindowOption({
          label: 'Select contact window',
          value: '',
        });
        setSelectedContactWindow(null);
      } finally {
        setIsLoadingContactWindows(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const weekdayLabels = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];
  const daysLabel =
    Array.isArray(selectedContactWindow?.days) &&
    selectedContactWindow?.days.length
      ? selectedContactWindow.days
          .slice()
          .sort((a: number, b: number) => a - b)
          .map((d: number) => weekdayLabels[d])
          .join(', ')
      : 'Mon, Tues, Wed, Thurs, Fri';

  const startTime = selectedContactWindow?.startTime;
  const endTime = selectedContactWindow?.endTime;
  const startTimeLabel = startTime ? formatTimeLabel(startTime) : '9:00am';
  const endTimeLabel = endTime ? formatTimeLabel(endTime) : '7:00pm';

  const normalizeReferenceId = (value: string) =>
    value
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '');

  const nextDisabled =
    !name ||
    isLoadingContactWindows ||
    contactWindowOptions.length === 0 ||
    !selectedContactWindowOption.value;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create campaign"
      actionButton={
        <Button
          onClick={() => {
            onNext?.({
              name,
              description,
              contactWindow: String(selectedContactWindowOption.value),
            });
            if (name) {
              const normalized = normalizeReferenceId(name);
              const referenceId = `${normalized}-${uuidv4()}`;
              const campaignInit: CampaignInit = {
                name,
                description,
                referenceId,
                contactWindowId: String(selectedContactWindowOption.value),
              };
              navigate('/beta/campaigns/new', {
                state: {
                  campaignInit,
                },
              });
            }
          }}
          disabled={nextDisabled}
        >
          Next
        </Button>
      }
      className="lg:max-w-[550px]"
    >
      <div className="space-y-4 pr-2">
        <div>
          <Label>Campaign name *</Label>
          <Input
            placeholder="Enter campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <Label>Campaign description</Label>
          <Textarea
            placeholder="Enter campaign description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div>
          <Label>Campaign contact window</Label>

          <div className="flex flex-row gap-2 justify-between">
            <div>
              {isLoadingContactWindows ? (
                <div className="flex gap-2 items-center py-2">
                  <Loading size="6" />
                  <span className="text-neutral-400 text-sm">
                    Loading contact windows...
                  </span>
                </div>
              ) : contactWindowOptions.length > 0 ? (
                <div className="flex gap-3 items-center w-full">
                  <SelectBox
                    className="w-full"
                    options={contactWindowOptions}
                    defaultValue={selectedContactWindowOption}
                    onChange={(value) => {
                      const selectedOption = contactWindowOptions.find(
                        (o) => o.value === value,
                      );
                      if (selectedOption) {
                        setSelectedContactWindowOption(selectedOption);
                        const matchingRecord = contactWindows.find(
                          (record) => record.id === value,
                        );
                        setSelectedContactWindow(matchingRecord || null);
                      }
                    }}
                  />
                </div>
              ) : null}
            </div>

            {selectedContactWindow && (
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/beta/campaigns/contact-window?campaignName=${name}&campaignDescription=${description}`,
                  )
                }
                size="small"
              >
                Create contact window
              </Button>
            )}
          </div>

          {selectedContactWindow ? (
            <div className="mt-3 rounded border p-3 text-sm text-neutral-700">
              <div className="flex items-center gap-2">
                <span className="font-medium">{daysLabel}</span>
                <span>•</span>
                <span>
                  {startTimeLabel} - {endTimeLabel}
                </span>
              </div>
              <div>
                <a
                  className="text-primary hover:underline cursor-pointer"
                  onClick={() => {
                    const params = new URLSearchParams(
                      selectedContactWindowOption.value
                        ? {
                            id: String(selectedContactWindowOption.value),
                            campaignName: name,
                            campaignDescription: description,
                          }
                        : {
                            campaignName: name,
                            campaignDescription: description,
                          },
                    );
                    const search = params.toString();
                    navigate(
                      `/beta/campaigns/contact-window${search ? `?${search}` : ''}`,
                    );
                  }}
                >
                  Edit contact window
                </a>
              </div>
            </div>
          ) : (
            <div className="mt-3 rounded border p-3 text-sm text-neutral-700">
              <div className="flex items-center justify-between gap-2">
                <span>No contact window configured.</span>
                <Button
                  size="small"
                  onClick={() =>
                    navigate(
                      `/beta/campaigns/contact-window?campaignName=${name}&campaignDescription=${description}`,
                    )
                  }
                >
                  Create contact window
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreateCampaignDialog;
